import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import {
  aplicarValidadoresConexao,
  controlesConexao,
  credencialPadrao,
  montarConfig,
  montarCredenciais,
  opcoesCredencial,
  rotuloCredencial,
  valoresDaConexao,
  valoresPadraoConexao
} from '../../../../components/smartinteg/conexao-campos/conexao-form.util';
import { Provedor, ProvedorConexao, TipoCredencial, TipoProvedor } from '../../../../types/smartinteg';

@Component({
  selector: 'app-provedor-conexoes',
  templateUrl: './provedor-conexoes.component.html',
  styleUrls: ['./provedor-conexoes.component.css'],
  providers: [ConfirmationService]
})
export class ProvedorConexoesComponent implements OnInit {

  provedorId!: string;
  provedor: Provedor | null = null;
  conexoes: ProvedorConexao[] = [];
  loading: boolean = true;
  salvando: boolean = false;

  conexaoDialog: boolean = false;
  isEditDialog: boolean = false;
  conexaoEmEdicao: ProvedorConexao | null = null;

  form!: FormGroup;

  TipoProvedor = TipoProvedor;
  TipoCredencial = TipoCredencial;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group(controlesConexao());
    this.form.get('tipoCredencial')?.valueChanges.subscribe(() => this.aplicarValidadores());
    this.form.get('alterarCredencial')?.valueChanges.subscribe((alterar: boolean) => this.aoAlternarCredencial(alterar));

    this.provedorId = this.route.snapshot.paramMap.get('provedorId')!;
    if (this.provedorId) {
      this.carregarProvedor();
      this.carregarConexoes();
    } else {
      this.toastService.erro('Erro', 'ID do provedor não encontrado.');
      this.voltar();
    }
  }

  carregarProvedor(): void {
    this.smartintegService.buscarProvedor(this.provedorId).subscribe({
      next: (data) => this.provedor = data,
      error: (err) => this.toastService.erro('Erro', 'Falha ao buscar dados do provedor: ' + mensagemErroApi(err))
    });
  }

  carregarConexoes(): void {
    this.loading = true;
    this.smartintegService.listarConexoesPorProvedor(this.provedorId).subscribe({
      next: (data) => {
        this.conexoes = data;
        this.loading = false;
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Falha ao carregar conexões: ' + mensagemErroApi(err));
        this.loading = false;
      }
    });
  }

  private aplicarValidadores(): void {
    if (this.provedor) {
      aplicarValidadoresConexao(this.form, this.provedor.tipo, this.isEditDialog);
    }
  }

  // Ao ligar "Alterar credencial" parte do tipo atual (se o provedor o aceita); ao desligar descarta o que foi digitado
  private aoAlternarCredencial(alterar: boolean): void {
    if (!this.provedor) return;
    if (alterar) {
      const atual = this.conexaoEmEdicao?.tipoCredencial;
      const aceito = opcoesCredencial(this.provedor.tipo).some(o => o.value === atual);
      this.form.patchValue({ tipoCredencial: aceito ? atual : credencialPadrao(this.provedor.tipo) });
    } else {
      this.form.patchValue({ cred_user: '', cred_password: '', cred_token: '' }, { emitEvent: false });
    }
    this.aplicarValidadores();
  }

  novaConexao(): void {
    if (!this.provedor) return;
    this.conexaoEmEdicao = null;
    this.isEditDialog = false;
    this.form.reset(valoresPadraoConexao(this.provedor.tipo));
    this.aplicarValidadores();
    this.conexaoDialog = true;
  }

  editarConexao(conexao: ProvedorConexao): void {
    if (!this.provedor) return;
    this.conexaoEmEdicao = conexao;
    this.isEditDialog = true;
    this.form.reset({ ...valoresPadraoConexao(this.provedor.tipo), ...valoresDaConexao(conexao) });
    this.aplicarValidadores();
    this.conexaoDialog = true;
  }

  salvarConexao(): void {
    if (!this.provedor) return;

    if (this.form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios');
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const tipo = this.provedor.tipo;
    const api = tipo === TipoProvedor.APIREST;
    this.salvando = true;

    if (!this.isEditDialog) {
      const data = {
        provedorId: this.provedorId,
        nome: v.nomeConexao,
        ambiente: v.ambiente,
        tipoCredencial: v.tipoCredencial,
        urlBase: api ? v.urlBase : undefined,
        padrao: v.padrao,
        config: montarConfig(v, tipo),
        credenciais: montarCredenciais(v)
      };

      this.smartintegService.criarConexao(data).subscribe({
        next: () => this.aposSalvar('Conexão criada com sucesso'),
        error: (err) => this.aoFalharSalvar('criar', err)
      });
      return;
    }

    // Sempre reenvia o objeto completo (id, nome, ambiente, urlBase, ativo, padrao, config); o config parte do
    // carregado para preservar chaves que a tela não edita. A credencial só segue se o usuário optou por
    // alterá-la: o backend a substitui por inteiro (sem merge) e SEM_CREDENCIAL remove a existente.
    const atual = this.conexaoEmEdicao!;
    const data: Record<string, any> = {
      id: atual.id,
      nome: v.nomeConexao,
      ambiente: v.ambiente,
      urlBase: api ? v.urlBase : (atual.urlBase ?? null),
      ativo: v.ativo,
      padrao: v.padrao,
      config: montarConfig(v, tipo, atual.config ?? {})
    };
    if (v.alterarCredencial) {
      data['tipoCredencial'] = v.tipoCredencial;
      if (v.tipoCredencial !== TipoCredencial.SEM_CREDENCIAL) {
        data['credenciais'] = montarCredenciais(v);
      }
    }

    this.smartintegService.atualizarConexao(data).subscribe({
      next: () => this.aposSalvar('Conexão atualizada com sucesso'),
      error: (err) => this.aoFalharSalvar('atualizar', err)
    });
  }

  private aposSalvar(mensagem: string): void {
    this.toastService.sucesso('Sucesso', mensagem);
    this.salvando = false;
    this.conexaoDialog = false;
    this.carregarConexoes();
  }

  private aoFalharSalvar(acao: string, err: any): void {
    this.toastService.erro('Erro', `Erro ao ${acao} conexão: ` + mensagemErroApi(err));
    this.salvando = false;
  }

  // O backend faz soft delete sem checar dependentes: sem esta verificação, endpoints e comandos
  // continuariam listados e executando contra uma conexão excluída
  deletarConexao(conexao: ProvedorConexao): void {
    forkJoin({
      endpoints: this.smartintegService.listarEndpointsPorConexao(conexao.id),
      comandos: this.smartintegService.listarComandos()
    }).subscribe({
      next: ({ endpoints, comandos }) => {
        const comandosDaConexao = comandos.filter(c => c.conexaoId === conexao.id);
        if (endpoints.length > 0 || comandosDaConexao.length > 0) {
          const usos: string[] = [];
          if (endpoints.length > 0) usos.push(`${endpoints.length} endpoint(s)`);
          if (comandosDaConexao.length > 0) usos.push(`${comandosDaConexao.length} comando(s)`);
          this.toastService.alerta(
            'Atenção',
            `A conexão "${conexao.nome}" está em uso por ${usos.join(' e ')}. Remova-os antes de excluir a conexão.`
          );
          return;
        }
        this.confirmarExclusao(conexao);
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Erro ao verificar o uso da conexão: ' + mensagemErroApi(err));
      }
    });
  }

  private confirmarExclusao(conexao: ProvedorConexao): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar a conexão <strong>${conexao.nome}</strong>?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarConexao(conexao.id).subscribe({
          next: () => {
            this.toastService.sucesso('Sucesso', 'Conexão deletada com sucesso');
            this.carregarConexoes();
          },
          error: (err) => {
            this.toastService.erro('Erro', 'Erro ao deletar conexão: ' + mensagemErroApi(err));
            this.loading = false;
          }
        });
      }
    });
  }

  urlOuHost(conexao: ProvedorConexao): string {
    return conexao.config?.url_base || conexao.urlBase || conexao.config?.host || '';
  }

  rotuloCredencialLista(conexao: ProvedorConexao): string {
    return conexao.tipoCredencial ? rotuloCredencial(conexao.tipoCredencial) : 'Sem credencial';
  }

  // Sem credencial só é problema em banco (precisa de usuário e senha); a API roda sem token estático
  credencialSuspeita(conexao: ProvedorConexao): boolean {
    return !conexao.tipoCredencial && this.provedor?.tipo === TipoProvedor.BASEDADOS;
  }

  irParaEndpoints(conexao: ProvedorConexao): void {
    this.router.navigate(['/smartinteg/provedores', this.provedorId, 'conexoes', conexao.id, 'endpoints']);
  }

  fecharDialog(): void {
    this.conexaoDialog = false;
  }

  voltar(): void {
    this.router.navigate(['/smartinteg/provedores']);
  }
}
